import { type JSX, useEffect } from "react";

import {
  CpqCore,
  CpqQueryClientProvider,
  OverridableComponentsKeys,
  StandardComponents,
} from "@4pace-cpq/core";
import Button from "@mui/material/Button";
import { QueryClientProvider } from "@tanstack/react-query";

import { cpqPath, queryClient } from "./App";
import AdditionalQuoteFields from "./components/AdditionalQuoteFields/AdditionalQuoteFields";
import { DebugLoginButtons } from "./components/DebugLoginButtons/DebugLoginButtons";
import { CartIdRouteComponentWrapper } from "./cpq/overrides/CartIdRouteComponentWrapper/CartIdRouteComponentWrapper";
import { CloneItemIconButtonWrapper } from "./cpq/overrides/CloneItemIconButtonWrapper/CloneItemIconButtonWrapper";
import { CustomIndicatorConfigurableProductInLineItem } from "./cpq/overrides/CustomIndicatorConfigurableProductInLineItem/CustomIndicatorConfigurableProductInLineItem";
import { CustomItemGroupActionButtonsArea } from "./cpq/overrides/CustomItemGroupActionButtonsArea";
import { DeleteItemIconButtonWrapper } from "./cpq/overrides/DeleteItemIconButtonWrapper/DeleteItemIconButtonWrapper";
import { LineItemAccordion } from "./cpq/overrides/LineItemAccordion/LineItemAccordion";
import { MinimalApp } from "./renderer/MinimalApp";
import { ConfigurationFinishedContextProvider } from "./renderer/controls/ConfigurationFinishedPage/ConfigurationFinishedContextProvider";
import { IsFastConfigurationContextProvider } from "./renderer/controls/IsFastConfigurationContext/IsFastConfigurationContextProvider";
import { LoadingSpinner } from "./tools/LoadingSpinner";

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

const cfgSessionId = urlParams.get("cfgSessionId");
const userSessionId = urlParams.get("userSessionId");
const fastConfiguration = urlParams.get("schnellkonfigurator");
export const language = urlParams.get("language");
export const iframeId = urlParams.get("iframeId");

export const isInIframe = window.location !== window.parent.location;

export const isDevEnvironment =
  window.location.href.includes("stag") ||
  window.location.href.includes("dev") ||
  window.location.href.includes("localhost");

export function Router(): JSX.Element {
  const isInCPQ = window.location.pathname.includes(cpqPath);

  useEffect(() => {
    if (cfgSessionId) {
      window.sessionStorage.setItem("cfgSessionId", cfgSessionId);
    }
    if (userSessionId) {
      window.sessionStorage.setItem("userSessionId", userSessionId);
    }
    if (fastConfiguration) {
      window.sessionStorage.setItem("fastConfiguration", fastConfiguration);
    } else {
      window.sessionStorage.setItem("fastConfiguration", "false");
    }
  });

  return (
    <>
      {!isInCPQ && (
        <>
          {!isInIframe && (
            <Button
              sx={{ marginBlock: "0.5rem" }}
              href={cpqPath + "/opportunities/"}
            >
              Go to CPQ
            </Button>
          )}
          <QueryClientProvider client={queryClient}>
            <ConfigurationFinishedContextProvider>
              <IsFastConfigurationContextProvider>
                {!isInIframe && isDevEnvironment && <DebugLoginButtons />}
                <MinimalApp />
              </IsFastConfigurationContextProvider>
            </ConfigurationFinishedContextProvider>
          </QueryClientProvider>
        </>
      )}

      {isInCPQ && (
        <>
          {!isInIframe && isDevEnvironment && (
            <Button sx={{ marginLeft: "2rem" }} href="/">
              Go Back
            </Button>
          )}
          <CpqQueryClientProvider>
            <CpqCore
              authMode="none"
              basePath={cpqPath}
              componentsOverride={{
                [OverridableComponentsKeys.AbsoluteProgressIndicator]:
                  LoadingSpinner,
                [OverridableComponentsKeys.CartView]: ({ item }) => (
                  <StandardComponents.CartView item={item} viewOnly={false} />
                ),
                [OverridableComponentsKeys.CartViewFooter]:
                  CustomItemGroupActionButtonsArea,
                [OverridableComponentsKeys.IndicatorConfigurableProductInLineItem]:
                  CustomIndicatorConfigurableProductInLineItem,
                [OverridableComponentsKeys.CloneItemIconButton]:
                  CloneItemIconButtonWrapper,
                [OverridableComponentsKeys.DeleteItemIconButton]:
                  DeleteItemIconButtonWrapper,
                [OverridableComponentsKeys.LineItemAccordion]:
                  LineItemAccordion,
                [OverridableComponentsKeys.PageHeading]: ({ text }) => (
                  <StandardComponents.PageHeading text={text} />
                ),
                [OverridableComponentsKeys.CartViewPageSubHeading]:
                  AdditionalQuoteFields,
                [OverridableComponentsKeys.CartIdRouteComponent]:
                  CartIdRouteComponentWrapper,
                [OverridableComponentsKeys.GenericSearchField]: () => <></>,
                [OverridableComponentsKeys.CartButtons]: () => <></>,
                [OverridableComponentsKeys.ShoppingCartSummary]: () => <></>,
                [OverridableComponentsKeys.GoBackToCatalogButton]: () => <></>,
                [OverridableComponentsKeys.AppInitializer]: () => null,
              }}
            />
          </CpqQueryClientProvider>
        </>
      )}
    </>
  );
}
